import type { ReadonlyRecord } from "@core/foundation/types";
import { isNullish } from "@core/foundation/types";
import type { ZodType } from "@core/zod";
import { z } from "@core/zod";

import { OAUTH_CONSENT_PATH } from "@platform/core";

import { ProjectIdSchema } from "@apis/memory/models";

const ROUTES = {
  AuthCallback: "/auth/callback",
  Dashboard: "/",
  Generations: "/generations",
  GithubConnections: "/github/connections",
  GithubConnectionsCallback: "/github/connections/callback",
  Media: "/media",
  MemoryProject: "/memory/projects/:projectId",
  MemoryProjectComments: "/memory/projects/:projectId/comments",
  MemoryProjects: "/memory/projects",
  OAuthConsent: OAUTH_CONSENT_PATH,
  Signin: "/auth/signin",
} as const;

const ROUTE_PARAMS = {
  MemoryProject: z.object(
    { projectId: ProjectIdSchema },
    { projectId: "The id of the memory project the page shows" },
  ),
  MemoryProjectComments: z.object(
    { projectId: ProjectIdSchema },
    { projectId: "The id of the memory project whose comments the page shows" },
  ),
} as const;

export type ParameterizedRoute = keyof typeof ROUTE_PARAMS;

type StaticRoute = Exclude<keyof typeof ROUTES, ParameterizedRoute>;

type Route = ParameterizedRoute | StaticRoute;

export type RouteParams = {
  readonly [K in ParameterizedRoute]: ReturnType<
    (typeof ROUTE_PARAMS)[K]["parse"]
  >;
};

function getPattern(route: Route): string {
  return ROUTES[route];
}

function getUrl(route: StaticRoute): string;
function getUrl<R extends ParameterizedRoute>(
  route: R,
  params: RouteParams[R],
): string;
function getUrl(route: Route, params?: ReadonlyRecord<string, string>): string {
  const pattern = ROUTES[route];

  if (isNullish(params)) {
    return pattern;
  }

  return pattern.replaceAll(/:(\w+)/gu, (_, key: string) => params[key] ?? "");
}

const ROUTE_PARAM_SCHEMAS: {
  readonly [K in ParameterizedRoute]: ZodType<RouteParams[K]>;
} = ROUTE_PARAMS;

function parseParams<R extends ParameterizedRoute>(
  route: R,
  rawParams: unknown,
): RouteParams[R] {
  return ROUTE_PARAM_SCHEMAS[route].parse(rawParams);
}

export const router = { getPattern, getUrl, parseParams } as const;
